import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { resolveMediaUrl } from "../../utils/marketingTemplateUtils";
import "./dashboard-bottom-panels.css";

/**
 * Figma section after Sales/Discounts/Charges:
 * Top sellers | Total In/Out Sales
 * SMS Details | Reservations
 */

const PLACEHOLDER_SELLERS = [
  {
    id: 1,
    name: "Chicken Karahi",
    portion: "Half",
    price: 35,
    image: "/assets/img/chickenwing.jpg",
  },
  {
    id: 2,
    name: "Butter Chicken",
    portion: "",
    price: 45,
    image: "/assets/img/pizza.png",
  },
  {
    id: 3,
    name: "Mutton Shanks",
    portion: "6 Piece",
    price: 85,
    image: "/assets/img/fires.png",
  },
  {
    id: 4,
    name: "Chicken Karahi",
    portion: "Full",
    price: 35,
    image: "/assets/img/chickenwing.jpg",
  },
];

const PLACEHOLDER_SALES = {
  inSales: [
    { key: "dineIn", label: "Dine-ins", value: 0 },
    { key: "reservations", label: "Reservations", value: 0 },
  ],
  outSales: [
    { key: "collection", label: "Collection", value: 0 },
    { key: "delivery", label: "Delivery", value: 0 },
  ],
};

const PLACEHOLDER_SMS = {
  total: 125,
  used: 75,
  left: 50,
  packageName: "Premium Offer",
  activationDate: "Oct 12, 2026",
};

const PLACEHOLDER_RESERVATIONS = [
  {
    id: 1,
    name: "Timmy Joe",
    when: "Oct 20 - 17:30",
    guests: 4,
    table: "DR-05",
  },
  {
    id: 2,
    name: "Mark Bill",
    when: "Oct 20 - 18:00",
    guests: 2,
    table: "DR-02",
  },
  {
    id: 3,
    name: "Sarah Lee",
    when: "Oct 20 - 19:15",
    guests: 6,
    table: "DR-11",
  },
  {
    id: 4,
    name: "John Doe",
    when: "Oct 21 - 12:00",
    guests: 3,
    table: "DR-07",
  },
  {
    id: 5,
    name: "Anna Ray",
    when: "Oct 21 - 13:45",
    guests: 5,
    table: "DR-09",
  },
];

function formatMoney(value, symbol = "£") {
  const n = Number(value) || 0;
  return `${symbol}${n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Same pattern as ManageItems — absolute URLs pass through; relative paths get API base. */
function normalizeProductImageSrc(path) {
  if (!path) return "";
  const value = String(path).trim();
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://") || value.startsWith("data:")) {
    return value;
  }
  if (value.startsWith("/assets/")) return value;
  return resolveMediaUrl(value);
}

function mapTopProductsToSellers(topProducts) {
  if (!Array.isArray(topProducts) || topProducts.length === 0) return null;

  return topProducts.slice(0, 5).map((row, idx) => {
    const qty = Number(row.quantitySold ?? row.QuantitySold ?? 0) || 0;
    const gross = Number(row.grossSales ?? row.GrossSales ?? 0) || 0;
    const avg =
      Number(row.avgItemPrice ?? row.AvgItemPrice ?? 0) ||
      (qty > 0 ? gross / qty : 0);
    const imagePath =
      row.productImage ??
      row.ProductImage ??
      row.menuItem_Image ??
      row.MenuItem_Image ??
      "";

    return {
      id: row.menuItem_ID ?? row.MenuItem_ID ?? idx,
      name: row.productName ?? row.ProductName ?? "—",
      portion: row.variantName ?? row.VariantName ?? "",
      price: avg,
      image: normalizeProductImageSrc(imagePath),
    };
  });
}

function SellerThumb({ src, alt }) {
  const [failed, setFailed] = useState(false);
  const showImg = Boolean(src) && !failed;

  return (
    <div className="dashboard-bp__seller-thumb-wrap">
      {showImg ? (
        <img
          className="dashboard-bp__seller-thumb"
          src={src}
          alt={alt || ""}
          onError={() => setFailed(true)}
        />
      ) : (
        <i className="fa fa-utensils dashboard-bp__seller-thumb-fallback" aria-hidden="true" />
      )}
    </div>
  );
}

function DashboardBottomPanels({
  topProducts = null,
  sales = PLACEHOLDER_SALES,
  sms = null,
  reservations = null,
  currencySymbol = "£",
}) {
  const sellers = useMemo(() => {
    const mapped = mapTopProductsToSellers(topProducts);
    return mapped ?? PLACEHOLDER_SELLERS;
  }, [topProducts]);

  const smsData = sms ?? PLACEHOLDER_SMS;
  const reservationRows = Array.isArray(reservations)
    ? reservations
    : PLACEHOLDER_RESERVATIONS;

  return (
    <section className="dashboard-bp" aria-label="Sellers, sales, SMS and reservations">
      <div className="dashboard-bp__grid">
        {/* Top sellers — Figma: thumb+name (8px) | portion | price, space-between */}
        <article className="dashboard-bp__card dashboard-bp__sellers">
          <div className="dashboard-bp__card-head">
            <h2 className="dashboard-bp__title">Top sellers</h2>
            <Link to="/report/salesByProducts" className="dashboard-bp__link">
              View all
            </Link>
          </div>
          <ul className="dashboard-bp__seller-list">
            {sellers.map((item) => (
              <li key={item.id} className="dashboard-bp__seller-row">
                <div className="dashboard-bp__seller-product">
                  <SellerThumb src={item.image} alt={item.name} />
                  <span className="dashboard-bp__seller-name" title={item.name}>
                    {item.name}
                  </span>
                </div>
                <span className="dashboard-bp__seller-portion">{item.portion || ""}</span>
                <span className="dashboard-bp__seller-price">
                  {formatMoney(item.price, currencySymbol)}
                </span>
              </li>
            ))}
          </ul>
        </article>

        {/* Total In / Out Sales — Figma two-column card */}
        <article className="dashboard-bp__card dashboard-bp__inout">
          <div className="dashboard-bp__inout-cols">
            <div className="dashboard-bp__inout-col">
              <h3 className="dashboard-bp__inout-heading">Total In Sales</h3>
              <div className="dashboard-bp__inout-stats">
                {sales.inSales.map((row) => (
                  <div key={row.key} className="dashboard-bp__inout-stat">
                    <div className="dashboard-bp__inout-label">{row.label}</div>
                    <div className="dashboard-bp__inout-value">
                      {formatMoney(row.value, currencySymbol)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="dashboard-bp__inout-divider" aria-hidden="true" />
            <div className="dashboard-bp__inout-col">
              <h3 className="dashboard-bp__inout-heading">Total Out Sales</h3>
              <div className="dashboard-bp__inout-stats">
                {sales.outSales.map((row) => (
                  <div key={row.key} className="dashboard-bp__inout-stat">
                    <div className="dashboard-bp__inout-label">{row.label}</div>
                    <div className="dashboard-bp__inout-value">
                      {formatMoney(row.value, currencySymbol)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* SMS Details — Figma exact */}
        <article className="dashboard-bp__card dashboard-bp__sms">
          <div className="dashboard-bp__sms-head">
            <h2 className="dashboard-bp__sms-title">SMS Details</h2>
            <Link
              to="/messaging/smsTopUps"
              className="dashboard-bp__sms-icon-btn"
              aria-label="Open SMS Top-ups"
              title="SMS Top-ups"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M2.5 2.5h11A1.5 1.5 0 0 1 15 4v6.5a1.5 1.5 0 0 1-1.5 1.5H8.2L5 14.5V12H2.5A1.5 1.5 0 0 1 1 10.5V4A1.5 1.5 0 0 1 2.5 2.5Z"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinejoin="round"
                />
                <path
                  d="M4.5 5.5h7M4.5 8h5"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                />
              </svg>
            </Link>
          </div>

          <div className="dashboard-bp__sms-stats">
            <div className="dashboard-bp__sms-stat">
              <div className="dashboard-bp__sms-num">{smsData.total}</div>
              <div className="dashboard-bp__sms-label">Total</div>
            </div>
            <div className="dashboard-bp__sms-vdiv" aria-hidden="true" />
            <div className="dashboard-bp__sms-stat">
              <div className="dashboard-bp__sms-num">{smsData.used}</div>
              <div className="dashboard-bp__sms-label">SMS used</div>
            </div>
            <div className="dashboard-bp__sms-vdiv" aria-hidden="true" />
            <div className="dashboard-bp__sms-stat">
              <div className="dashboard-bp__sms-num">{smsData.left}</div>
              <div className="dashboard-bp__sms-label">SMS Left</div>
            </div>
          </div>

          <div className="dashboard-bp__sms-rule" aria-hidden="true" />

          <div className="dashboard-bp__sms-offer">
            <div className="dashboard-bp__sms-offer-block">
              <div className="dashboard-bp__offer-title">{smsData.packageName}</div>
              <div className="dashboard-bp__offer-sub">Active package</div>
            </div>
            <div className="dashboard-bp__sms-offer-block dashboard-bp__sms-offer-block--right">
              <div className="dashboard-bp__offer-title">{smsData.activationDate}</div>
              <div className="dashboard-bp__offer-sub">Activation date</div>
            </div>
          </div>
        </article>

        {/* Reservations */}
        <article className="dashboard-bp__card dashboard-bp__reservations">
          <div className="dashboard-bp__card-head">
            <h2 className="dashboard-bp__title">Reservations</h2>
            <Link to="/tableManagement/reservation" className="dashboard-bp__link">
              View all
            </Link>
          </div>
          {reservationRows.length > 0 ? (
            <ul className="dashboard-bp__res-list">
              {reservationRows.map((row) => (
                <li key={row.id} className="dashboard-bp__res-row">
                  <span className="dashboard-bp__res-name">{row.name}</span>
                  <span className="dashboard-bp__res-when">{row.when}</span>
                  <span className="dashboard-bp__res-guests">{row.guests} Guests</span>
                  <span className="dashboard-bp__res-table">{row.table}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="dashboard-bp__empty">No upcoming reservations.</div>
          )}
        </article>
      </div>
    </section>
  );
}

export default DashboardBottomPanels;
